import React, { useState, useMemo, useEffect } from 'react'
import {
  Input,
  Form,
  Typography,
  message,
  InputNumber
} from 'antd'
import {
  MinusOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { useMsal } from '@azure/msal-react'

import Modal from '../../../../components/common/modal/modal'
import Button from '../../../../components/common/button/button'
import { createCashCount } from '../../../../api/cashCountApiService/cashCountApiUtils'
import './cashCountModal.scss'

const { TextArea } = Input
const { Text } = Typography

const CashCountModal = ({ visible, onClose, onSuccess }) => {
  const { instance, accounts } = useMsal()
  const [loading, setLoading] = useState(false)
  const [responseMessage, setResponseMessage] = useState('')

  // Get system cash on hand from window object (same as in CashCount component)
  const [systemCashOnHand, setSystemCashOnHand] = useState(0)

  // Define default denominations (all set to zero)
  const defaultDenominations = {
    '1000.00': 0,
    '500.00': 0,
    '200.00': 0,
    '100.00': 0,
    '50.00': 0,
    '20.00': 0,
    '10.00': 0,
    '5.00': 0,
    '1.00': 0,
    '0.25': 0,
    '0.10': 0,
    '0.05': 0
  }

  // Define denominations and their counts
  const [denominations, setDenominations] = useState(defaultDenominations)
  const [notes, setNotes] = useState('')
  const [checkerName, setCheckerName] = useState('') // State for checker name

  // Get the logged-in user's name
  const getLoggedInUserName = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0]
      return account.name || account.username || 'Unknown User'
    }
    return 'Unknown User'
  }

  // Effect to get system cash on hand from window object
  useEffect(() => {
    // Check if window object contains systemCashOnHand data
    if (window.pettySystemCashOnHand !== undefined) {
      setSystemCashOnHand(window.pettySystemCashOnHand || 0)
    }

    // Set up listener for updates to system cash on hand
    const handleSystemCashUpdate = (e) => {
      if (e.detail && e.detail.systemCashOnHand !== undefined) {
        setSystemCashOnHand(e.detail.systemCashOnHand)
      }
    }

    // Add event listener
    window.addEventListener('systemCashOnHandUpdated', handleSystemCashUpdate)

    // Clean up
    return () => {
      window.removeEventListener('systemCashOnHandUpdated', handleSystemCashUpdate)
    }
  }, [])

  // Reset the form when the modal becomes visible
  useEffect(() => {
    if (visible) {
      // Reset to default values when modal opens
      setDenominations(defaultDenominations)
      setNotes('')
      // Set the logged-in user's name as default checker name
      setCheckerName(getLoggedInUserName())
      setResponseMessage('')
    }
  }, [visible, accounts])

  // Function to handle changing denomination counts via input
  const handleDenominationChange = (denom, value) => {
    // Ensure the value is a positive number or zero
    const newValue = value === null || value === undefined || value < 0 ? 0 : Math.floor(value)

    setDenominations(prev => ({
      ...prev,
      [denom]: newValue
    }))
  }

  // Function to handle changing denomination counts via steppers
  const changeDenomCount = (denom, change) => {
    setDenominations(prev => ({
      ...prev,
      [denom]: Math.max(0, prev[denom] + change)
    }))
  }

  // Calculate each denomination's total and the overall total
  const calculatedData = useMemo(() => {
    let grandTotal = 0
    const data = Object.entries(denominations).map(([denom, count]) => {
      const value = parseFloat(denom)
      const total = value * count
      grandTotal += total

      return {
        denomination: parseFloat(denom),
        quantity: count,
        total: total.toFixed(2)
      }
    }).sort((a, b) => b.denomination - a.denomination)

    // Calculate discrepancy using system cash on hand instead of hardcoded value
    const discrepancy = grandTotal - systemCashOnHand
    const discrepancyType = discrepancy > 0 ? 'surplus' : discrepancy < 0 ? 'shortage' : 'balanced'

    return {
      data,
      grandTotal: grandTotal.toFixed(2),
      discrepancy: discrepancy.toFixed(2),
      discrepancyType,
      idealPettyCash: systemCashOnHand.toFixed(2)
    }
  }, [denominations, systemCashOnHand])

  // Check if any denomination has at least 1 quantity and notes are provided
  const hasEntries = useMemo(() => {
    return Object.values(denominations).some(count => count > 0)
  }, [denominations])

  // Check if form is valid for submission (now includes checker name)
  const isFormValid = useMemo(() => {
    return hasEntries && notes.trim().length > 0 && checkerName.trim().length > 0
  }, [hasEntries, notes, checkerName])

  // Submit handler
  const handleSubmit = async () => {
    try {
      setLoading(true)
      setResponseMessage('')

      // Format data for API according to the specified payload structure
      const formattedDenominations = calculatedData.data.map(item => ({
        denomination: item.denomination,
        quantity: item.quantity,
        total: parseFloat(item.total)
      }))

      const cashCountData = {
        denominations: formattedDenominations,
        totalAmount: parseFloat(calculatedData.grandTotal),
        notes: notes,
        countedBy: checkerName.trim()
      }

      // Submit to API
      const response = await createCashCount(cashCountData, instance)

      setResponseMessage(response.message || 'Cash count submitted successfully')

      // Show success message as toast
      message.success({
        content: response.message || 'Cash count has been successfully recorded.',
        duration: 3,
        className: 'custom-message-toast'
      })

      // Close modal after short delay and call onSuccess if provided
      setTimeout(() => {
        if (onSuccess) {
          onSuccess() // Call the onSuccess handler to refresh the data
        }
        onClose()
      }, 1500)

    } catch (error) {
      console.error('Error submitting cash count:', error)
      message.error({
        content: error.message || 'An error occurred while submitting the cash count.',
        duration: 3,
        className: 'custom-message-toast'
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle cancel with reset
  const handleCancel = () => {
    onClose()
  }

  // Render a single denomination row with input control and steppers
  const renderDenominationRow = (item) => (
    <div key={item.denomination} className="denomination-row">
      <span className="denomination-label">PHP {item.denomination.toFixed(2)}</span>
      <div className="count-controls">
        <Button
          type="primary"
          shape="circle"
          icon={<MinusOutlined />}
          className="decrement-btn"
          onClick={() => changeDenomCount(item.denomination.toFixed(2), -1)}
          size="small"
          disabled={item.quantity <= 0}
        />
        <InputNumber
          min={0}
          value={item.quantity}
          onChange={(value) => handleDenominationChange(item.denomination.toFixed(2), value)}
          className="count-input"
          size="small"
          precision={0}
          placeholder="0"
          style={{ width: '35px', margin: '0 4px' }}
          controls={false}
          parser={(value) => value.replace(/[^\d]/g, '')}
          formatter={(value) => value}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
              e.preventDefault()
            }
          }}
        />
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          className="increment-btn"
          onClick={() => changeDenomCount(item.denomination.toFixed(2), 1)}
          size="small"
        />
      </div>
      <span className="denomination-total">PHP {item.total}</span>
    </div>
  )

  // Function to render discrepancy message
  const renderDiscrepancyMessage = () => {
    const { discrepancy, discrepancyType } = calculatedData
    const absDiscrepancy = Math.abs(parseFloat(discrepancy))

    if (discrepancyType === 'balanced') {
      return null // No message when balanced
    }

    return (
      <div className={`discrepancy-message ${discrepancyType}`}>
        {discrepancyType === 'surplus'
          ? `Surplus: PHP ${absDiscrepancy.toFixed(2)}`
          : `Discrepancy: PHP ${absDiscrepancy.toFixed(2)}`
        }
      </div>
    )
  }

  return (
    <Modal
      title={<div className="modal-title">Cash Count Entry</div>}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      centered
      className="cash-count-entry-modal"
      destroyOnClose={true}
    >
      <Form layout="vertical" className="cash-count-form">
        {/* Checker Name Field with user's name as placeholder */}
        <Form.Item
          label={<span>Checker Name <span className="required-asterisk">*</span></span>}
          className="checker-field"
        >
          <Input
            placeholder={getLoggedInUserName()}
            value={checkerName}
            onChange={e => setCheckerName(e.target.value)}
            status={checkerName.trim().length === 0 ? 'error' : ''}
            maxLength={100}
          />
        </Form.Item>

        <Form.Item className="denominations-section">
          <Text strong className="section-title">Denominations</Text>
          <div className="denominations-container">
            <div className="denominations-header">
              <span className="header-denomination">Denomination</span>
              <span className="header-count">Count</span>
              <span className="header-total">Total</span>
            </div>
            {calculatedData.data.map(item => renderDenominationRow(item))}
            <div className="grand-total-row">
              <span className="grand-total-label">Total Amount:</span>
              <span className="grand-total-value">PHP {calculatedData.grandTotal}</span>
            </div>

            {/* Discrepancy Message */}
            {renderDiscrepancyMessage()}

            {/* Ideal Petty Cash Row - now shows System Cash on Hand */}
            <div className="ideal-cash-row">
              <span className="ideal-cash-label">Ideal Petty Cash:</span>
              <span className="ideal-cash-value">PHP {calculatedData.idealPettyCash}</span>
            </div>
          </div>
        </Form.Item>

        <Form.Item label={<span>Notes <span className="required-asterisk">*</span></span>} >
          <TextArea
            placeholder="Enter additional details (required)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 4 }}
            status={notes.trim().length === 0 ? 'error' : ''}
            maxLength={100}
            showCount
          />
        </Form.Item>

        {responseMessage && (
          <div className="response-message">
            {responseMessage}
          </div>
        )}

        <Form.Item>
          <div className="modal-actions">
            <Button
              type="primary"
              onClick={handleSubmit}
              className="submit-btn"
              loading={loading}
              disabled={!isFormValid}
            >
              Submit
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default CashCountModal